import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Award, CheckCircle2, ArrowRight, Sparkles, X, Clock,
} from 'lucide-react';

/**
 * Iter-225d / Phase 1C-A — Application-Time Certification Prompt.
 *
 * NON-BLOCKING card that surfaces above the Apply CTA on every shift detail
 * / one-tap-apply page. It tells the talent:
 *
 *   • IF the role has a published certification AND the talent already holds it:
 *       → small green "✓ You hold the {Badge}" pill.
 *
 *   • IF the role has a certification but the talent doesn't have it:
 *       → friendly card with benefits + "Take the assessment →" CTA.
 *         Application still proceeds normally — this is purely additive.
 *
 *   • IF the role has no certification module:
 *       → renders nothing (component returns null).
 *
 * Props:
 *   - shiftId        (required) — shift uuid
 *   - className      (optional)
 *   - compact        (optional) — render the slim variant (e.g. in modal)
 */
const CertificationPromptCard = ({ shiftId, className = '', compact = false }) => {
  const navigate = useNavigate();
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!shiftId) {
        setLoading(false);
        return;
      }
      try {
        const r = await api.get(`/training/role-suggestion/${shiftId}`);
        if (!cancelled) setSuggestion(r.data);
      } catch (_) {
        // Silent — this is an additive prompt. Never break the apply flow.
        if (!cancelled) setSuggestion(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shiftId]);

  if (loading || dismissed) return null;
  if (!suggestion || !suggestion.has_role_certification || !suggestion.module) return null;

  const { module, talent_status, earned_at, benefits, assessment_url } = suggestion;

  // ── Already certified — small green confirmation pill ───────────────
  if (talent_status === 'completed') {
    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium ${className}`}
        data-testid="cert-prompt-already-certified"
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        <span>You hold the {module.badge_name}</span>
        {earned_at && <span className="text-emerald-600">· earned {earned_at}</span>}
      </div>
    );
  }

  // ── In progress — encourage finish ──────────────────────────────────
  if (talent_status === 'in_progress') {
    return (
      <Card
        className={`border-l-4 border-l-amber-400 bg-amber-50/60 ${className}`}
        data-testid="cert-prompt-in-progress"
      >
        <CardContent className={compact ? 'p-3' : 'p-4'}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-amber-100 p-2 mt-0.5">
                <Clock className="w-4 h-4 text-amber-700" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900">
                  Finish your {module.badge_name} assessment
                </h4>
                <p className="text-xs text-gray-600 mt-0.5">
                  You started this earlier. About {module.estimated_minutes} minutes left.
                </p>
              </div>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="text-gray-400 hover:text-gray-600 -mt-1"
              aria-label="Dismiss"
              data-testid="cert-prompt-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              onClick={() => navigate(assessment_url)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="cert-prompt-resume-btn"
            >
              Resume assessment <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Not started — the main "Earn your badge" prompt ─────────────────
  return (
    <Card
      className={`border-l-4 border-l-cyan-500 bg-gradient-to-br from-cyan-50/60 to-white ${className}`}
      data-testid="cert-prompt-card"
    >
      <CardContent className={compact ? 'p-3' : 'p-4'}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <div className="rounded-full bg-cyan-100 p-2 mt-0.5">
              <Award className="w-4 h-4 text-cyan-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-sm font-semibold text-gray-900">
                  Earn your {module.badge_name}
                </h4>
                <Badge
                  variant="outline"
                  className="text-[10px] bg-cyan-100 text-cyan-800 border-cyan-200"
                >
                  Optional · Boost your application
                </Badge>
              </div>
              <p className="text-xs text-gray-600 mt-0.5">
                Strengthen your application — you can still apply without it.
              </p>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-gray-400 hover:text-gray-600 -mt-1"
            aria-label="Dismiss"
            data-testid="cert-prompt-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!compact && benefits?.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {benefits.slice(0, 3).map((b, i) => (
              <li
                key={i}
                className="text-xs text-gray-700 flex items-start gap-2"
                data-testid={`cert-prompt-benefit-${i}`}
              >
                <Sparkles className="w-3 h-3 text-cyan-500 mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() => navigate(assessment_url)}
            className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white"
            data-testid="cert-prompt-take-assessment-btn"
          >
            Take the assessment <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
          <span className="text-[11px] text-gray-500">
            ~{module.estimated_minutes} min · pass at {module.pass_score}%
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default CertificationPromptCard;
